// ====== TAX EXPORT HELPERS ======
// Shared utilities for generating Excel/PDF exports for Sổ sách & Tờ khai
// Heavy HTML template generation remains in the page component,
// but these helpers can be reused by other modules.

import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { BIZ_INDUSTRIES, QUARTER_LABELS, MONTHS_BY_QUARTER } from '../constants/taxConstants';

// Format date YYYY-MM-DD → DD/MM/YYYY
export const formatDateVN = (d) => {
  if (!d) return "";
  const p = d.split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
};

// Format number vi-VN
export const formatNumber = (n) => n ? n.toLocaleString("vi-VN") : "0";

// Sổ sách titles & mẫu số
export const SO_TITLES = {
  s1a: "SỔ CHI TIẾT DOANH THU BÁN HÀNG HÓA, DỊCH VỤ",
  s2a: "SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ",
  s2b: "SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ",
  s2c: "SỔ CHI TIẾT DOANH THU, CHI PHÍ",
  s2d: "SỔ CHI TIẾT VẬT LIỆU, DỤNG CỤ, SẢN PHẨM, HÀNG HÓA",
  s2e: "SỔ CHI TIẾT TIỀN",
  s3a: "SỔ THEO DÕI NGHĨA VỤ THUẾ KHÁC",
  bk_htk: "BẢNG KÊ HÀNG TỒN KHO, MÁY MÓC, THIẾT BỊ CỦA HỘ KINH DOANH, CÁ NHÂN KINH DOANH",
};

export const SO_MAU = {
  s1a: "S1a-HKD", s2a: "S2a-HKD", s2b: "S2b-HKD",
  s2c: "S2c-HKD", s2d: "S2d-HKD", s2e: "S2e-HKD",
  s3a: "S3a-HKD", bk_htk: "01/BK-HTK",
};

// Excel CSS chung
export const EXCEL_CSS = `<style>
@page{size:210mm 297mm;margin:20mm 15mm 20mm 15mm;mso-header-margin:10mm;mso-footer-margin:10mm;mso-page-orientation:portrait}
table{border-collapse:collapse;width:680px;margin:0 auto}
td,th{font-family:Arial;font-size:10pt;padding:5px 8px;border:1px solid #999;vertical-align:middle;mso-number-format:"\\\\@"}
.nb{border:none} .ct{text-align:center} .rt{text-align:right} .lt{text-align:left}
.num{text-align:right;font-family:'Courier New',monospace;mso-number-format:"\\\\#\\\\,\\\\#\\\\#0"}
.hdr{background:#D4E6F1;font-weight:bold;text-align:center;font-size:9pt}
.colhdr{background:#F0E8D8;font-weight:bold;text-align:center;font-size:8pt;color:#5D4E37}
.total{background:#FFF8E7;font-weight:bold}
.bold{font-weight:bold} .it{font-style:italic} .sm{font-size:8pt;color:#666}
</style>`;

// PDF CSS chung
export const PDF_CSS = `<style>
*{margin:0;padding:0;box-sizing:border-box}
@page{size:A4 portrait;margin:20mm 15mm}
body{font-family:Arial,sans-serif;font-size:10pt;color:#222;padding:20mm 15mm}
table{border-collapse:collapse;width:100%}
td,th{padding:5px 8px;border:1px solid #aaa;vertical-align:middle;font-size:10pt}
.nb{border:none} .ct{text-align:center} .rt{text-align:right}
.num{text-align:right;font-family:'Courier New',monospace}
.colhdr{background:#f0e8d8;font-weight:bold;text-align:center;font-size:9pt}
.total{background:#fff8e7;font-weight:bold}
.bold{font-weight:bold} .it{font-style:italic} .sm{font-size:8pt;color:#888}
h2{text-align:center;margin:8px 0 4px}
</style>`;

// Header chung cho sổ sách Excel
export function soExcelHeader(title, mau, businessInfo = {}) {
  const { hoTen = "", diaChiKD = "", mst = "" } = businessInfo;
  return `
<tr><td colspan="2" class="nb lt" style="font-size:9pt"><b>HỘ, CÁ NHÂN KINH DOANH: ${hoTen || "................"}</b><br/>Địa chỉ: ${diaChiKD || "................................"}<br/>Mã số thuế: ${mst || "................"}</td><td colspan="2" class="nb rt" style="font-size:9pt"><b>Mẫu số ${mau}</b><br/><i>(Kèm theo Thông tư số<br/>152/2025/TT-BTC<br/>ngày 31 tháng 12 năm 2025<br/>của Bộ trưởng Bộ Tài chính)</i></td></tr>
<tr><td colspan="4" class="nb">&nbsp;</td></tr>
<tr><td colspan="4" class="nb ct" style="font-size:14pt"><b>${title}</b></td></tr>
<tr><td colspan="4" class="nb">&nbsp;</td></tr>
<tr><td colspan="4" class="nb lt" style="font-size:9pt">Địa điểm kinh doanh: ${diaChiKD || "................................"}</td></tr>
<tr><td colspan="4" class="nb lt" style="font-size:9pt">Kỳ kê khai: Năm 2026</td></tr>
<tr><td colspan="4" class="nb">&nbsp;</td></tr>
<tr><td colspan="4" class="nb rt it" style="font-size:9pt"><i>Đơn vị tính: đồng</i></td></tr>`;
}

// Footer chung
export function soExcelFooter(colSpan) {
  return `
<tr><td colspan="${colSpan}" class="nb">&nbsp;</td></tr>
<tr><td colspan="${colSpan}" class="nb rt it sm">Ngày ..... tháng ..... năm 2026</td></tr>
<tr><td colspan="${colSpan}" class="nb rt bold" style="font-size:10pt">NGƯỜI ĐẠI DIỆN HỘ KINH DOANH/<br/>CÁ NHÂN KINH DOANH</td></tr>
<tr><td colspan="${colSpan}" class="nb rt it sm">(Ký, ghi rõ họ tên, đóng dấu (nếu có))</td></tr>`;
}

// Wrap HTML body thành Excel data URI
export function wrapExcelDataUri(body, sheetName) {
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head><meta charset="UTF-8">
<!--[if gte mso 9]><xml>
<x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>${sheetName}</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/><x:Print><x:ValidPrinterInfo/><x:PaperSizeIndex>9</x:PaperSizeIndex><x:FitWidth>1</x:FitWidth><x:FitHeight>32767</x:FitHeight><x:LeftMargin>0.59</x:LeftMargin><x:RightMargin>0.59</x:RightMargin><x:TopMargin>0.79</x:TopMargin><x:BottomMargin>0.79</x:BottomMargin></x:Print>
<x:FitToPage/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
</xml><![endif]-->
${EXCEL_CSS}</head><body style="margin:0;padding:0"><table>${body}</table></body></html>`;
  return "data:application/vnd.ms-excel;charset=utf-8;base64," + btoa(unescape(encodeURIComponent(html)));
}

// Print PDF helper
export function openPrintWindow(html) {
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

// Get file name for sổ sách
export function getSoFileName(soType) {
  return `So_${soType.toUpperCase()}_HKD.xlsx`;
}

// Get file name for tờ khai
export function getTkFileName(quarter) {
  return `ToKhai_01CNKD_${quarter.toUpperCase()}.xlsx`;
}

// ====== XLSX EXPORT (proper .xlsx format) ======

/**
 * Build header rows for sổ sách/tờ khai xlsx
 * @param {string} title - Tiêu đề sổ
 * @param {string} mau - Mẫu số (e.g. "S1a-HKD")
 * @param {Object} businessInfo - { hoTen, diaChiKD, mst }
 * @param {string} period - Kỳ kê khai
 * @param {number} colCount - Số cột (for merging)
 * @returns {Array<Array>} header rows
 */
export function xlsxHeaderRows(title, mau, businessInfo = {}, period = "", colCount = 4) {
  const { hoTen = "", diaChiKD = "", mst = "" } = businessInfo;
  return [
    [`HỘ, CÁ NHÂN KINH DOANH: ${hoTen || "................"}`, ...Array(colCount - 2).fill(null), `Mẫu số ${mau}`],
    [`Mã số thuế: ${mst || "................"}`, ...Array(colCount - 2).fill(null), "(Kèm theo Thông tư số 152/2025/TT-BTC"],
    [`Địa chỉ: ${diaChiKD || "................................"}`, ...Array(colCount - 2).fill(null), "ngày 31 tháng 12 năm 2025 của Bộ trưởng Bộ"],
    [null, ...Array(colCount - 2).fill(null), "Tài chính)"],
    Array(colCount).fill(null),
    [title, ...Array(colCount - 1).fill(null)],
    Array(colCount).fill(null),
    [`Địa điểm kinh doanh: ${diaChiKD || "................................"}`, ...Array(colCount - 1).fill(null)],
    [`Kỳ kê khai: ${period}`, ...Array(colCount - 1).fill(null)],
    Array(colCount).fill(null),
    [...Array(colCount - 1).fill(null), "Đơn vị tính: đồng Việt Nam"],
  ];
}

/**
 * Build footer rows for sổ sách xlsx
 * @param {number} colCount
 * @param {number} year
 * @returns {Array<Array>}
 */
export function xlsxFooterRows(colCount) {
  return [
    Array(colCount).fill(null),
    [...Array(colCount - 1).fill(null), "Ngày ... tháng ... năm ..."],
    [...Array(colCount - 1).fill(null), "NGƯỜI ĐẠI DIỆN HỘ KINH DOANH/"],
    [...Array(colCount - 1).fill(null), "CÁ NHÂN KINH DOANH"],
    [...Array(colCount - 1).fill(null), "(Ký, họ tên, đóng dấu)"],
  ];
}

/**
 * Build and download xlsx workbook
 * @param {Object} options
 * @param {string} options.fileName - File name (e.g. "SoSach_s1a_2026-Q1.xlsx")
 * @param {string} options.sheetName - Sheet tab name
 * @param {Array<Array>} options.rows - Array of row arrays (each cell value)
 * @param {Array<number>} options.colWidths - Column widths in characters
 */
export function buildAndDownloadXlsx({ fileName, sheetName, rows, colWidths }) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(rows);

  if (colWidths) {
    ws['!cols'] = colWidths.map(w => ({ wch: w }));
  }

  XLSX.utils.book_append_sheet(wb, ws, sheetName || "Sheet1");
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
}
